import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { IdbService } from './idb.service';
import { Command } from 'app/models/command';
import { ToolboxService } from 'app/toolbox.service';
import { SiteReviewService } from './site-review.service';
import { DataStoreService } from './datastore.service';
import { ChatterService } from 'app/chat/shared/services/chatter.service';
import { Router } from '@angular/router';
import { PhaseOverlayService } from './phase-overlay.service';

@Injectable({
  providedIn: 'root'
})
export class BulkLoaderService {

  constructor(private idb: IdbService, private tb: ToolboxService, private db: SiteReviewService, private dataStore: DataStoreService, private chatter: ChatterService, private router: Router, private phaseOverlay: PhaseOverlayService) { }

  /**
   * Optional filter for the rows that may be ADDED to the grid (IndexedDB still keeps every job).
   * A screen that shows a subset of the jobs (e.g. Active Work Orders, district 2) sets it on init and clears it on destroy.
   * Rows already in the grid are still updated; null means no filtering.
   */
  rowFilter: ((job: any) => boolean) | null = null;

  jobListTrigger = new BehaviorSubject(0)
  jobAuthTrigger = new ReplaySubject(1)
  percentage = new BehaviorSubject(0);
  public loader = new BehaviorSubject<number>(0);
  
  existingIds: Set<number> = new Set([]);
  loadedJobRecords = [];

  private batchSize = 100;
  private updateThreshold = 100;
  gridUpdatedCount = 0;

  get loader$() {
    return this.loader.asObservable();
  }


  async jsonBulkLoader(gridApi, gridOptions) {
    gridApi?.setGridOption('rowData', [] as any);
    this.loader.next(0);
    let progress = 0;

    const intervalId = setInterval(() => {
      progress += 20;
      this.loader.next(progress);
      if (progress >= 101) {
        clearInterval(intervalId);
      }
    }, 800);
    await this.loadOhMapCache();
    await this.idb.clearData('jobs');
    this.db.getJobsGoogleJson().subscribe({
      next: async (res) => {
        //console.log("getJobsGoogleJson ", res.length);
        let jsonFile: any = res;
        gridOptions.localeText.noRowsToShow = 'Loading complete...';
        if (Array.isArray(jsonFile) && jsonFile.length > 0) {
          // make sure to eliminate duplicates
          this.existingIds.clear();
          const uniqueJobs = new Set();
          let filteredJobs = jsonFile.filter((newRow: any) => {
            if (!uniqueJobs.has(newRow.jobID)) {
              uniqueJobs.add(newRow.jobID);
              return true;
            }
            return false;
          });
          //console.log('Filtered jobs:', filteredJobs);

          filteredJobs.forEach((job: any) => {
            this.existingIds.add(job.jobID);
            this.formatBulkLoaderJobs(job);
          });

          // Rules-based phases (Phase Manager): adds pm* fields to the district 2 jobs when the feature is on.
          await this.phaseOverlay.enrichAll(filteredJobs);

          if (filteredJobs.length > 0) {
            if(gridApi){
              filteredJobs = await this.clearRows(gridApi, filteredJobs);
              //console.log('Cleared rows:', filteredJobs.length);
              const rowsForGrid = this.rowFilter ? filteredJobs.filter(this.rowFilter) : filteredJobs;
              await gridApi.applyTransactionAsync({ add: rowsForGrid }, this.callback);
              //console.log('Rows added:', filteredJobs.length);
            }
            //gridApi?.setGridOption('rowData', filteredJobs as any);
            const today = new Date();
            const pstDateString = today.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
            localStorage.setItem('chunkerRunTodayPST', pstDateString); // Set the flag
            // then we update the local DB
            await this.idb.insertBulk('jobs', filteredJobs, async () => {
              //console.log('cache bulk done inserting ' + filteredJobs.length + ' rows');            
              this.updateLoader(gridApi,true);
            });
          }
        } else {
          console.warn("Received empty or invalid JSON data");
          clearInterval(intervalId);
          this.loader.next(100);
          gridOptions.localeText.noRowsToShow = 'No Rows to Show';
        }
      },
      error: (error) => {
        console.error("Error in getJobsGoogleJson:", error);
        clearInterval(intervalId);
        this.loader.next(100);
      }
    });
  }

  formatBulkLoaderJobs(job) {

    //if(job.jobID==45520){debugger}


    this.normalizeObjectKeys(job,["pMNumber","wAApprovalDate","wACurrent","wACurrentStatus","wADelta","wASubmissionDate","wAApprovalDate"
                              ,"wADescription","wATotal","pGEFieldEngineer","wAPricingStructure","pCNotes","uSADone","pGEFieldEngineer"
                              ,"wAPricingStructure","qAFailCount","qAInitialScore","qAReceived","qAInitialReceivedDate","qAScore","qAStatus"
                              ,"qASubmitted","qAReviewType","qAPassFail","wACurrent","cOPReceivedDate","cOPSubmittedDate","w1s","w2s","w3s"
                              ,"lMEComplete", "oHMap",])

    // if (job.updatedDate === null || job.updatedDate === undefined || job.updatedDate === '') {
    //   job.updatedDate = new Date(job.jobCreatedDate);
    // } else {
    //   job.updatedDate = new Date(job.updatedDate);
    // }
    
    
    // Fill oHMap from the local cache when the API did not send it
    this.restoreOhMap(job);

    if (job.updatedDate === null || job.updatedDate === undefined || job.updatedDate === '') {
      job.updatedDate = new Date(job.jobCreatedDate);
    } else {
      job.updatedDate = new Date(job.updatedDate);
    }
  }

  normalizeObjectKeys(obj, correctKeys) {
    for (const correctKey of correctKeys) {
        const lowerKey = correctKey.toLowerCase();

        // Find an incorrectly cased key in the object
        for (const key in obj) {
            if (key.toLowerCase() === lowerKey && key !== correctKey) {
                // If the incorrectly cased key has a value, assign it to the correct key
                if (obj[key] !== undefined && obj[key] !== '') {
                    obj[correctKey] = obj[key];
                }
                // Optionally delete the incorrect key after fixing
                delete obj[key];
            }
        }
    }
}



  async clearRows(gridApi, rows) {
    const rowNodesMap = new Map();
    gridApi.forEachNode(node => {
      rowNodesMap.set(String(node.data.jobID), node);
    });
  
    var filteredRecs = [];
  
    rows.forEach(row => {
      const node = rowNodesMap.get(String(row.jobID));
      if (node) {
        gridApi.applyTransaction({ remove: [node.data] });
      } else {
        filteredRecs.push(row);
      }
    });
    return filteredRecs;
  }

  async updateLoader(gridApi, forceexpire = false) {
    try {
      let ed: Date = new Date("1/1/2024");
      const dbLastModDate = await this.idb.updateStatus('first');
  
      if (forceexpire) {
        let d = new Date();
        ed = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 5);
      } else {
        if (dbLastModDate.length === 0) {
          return null;
        }
        ed = new Date(dbLastModDate[0]);
      }
  
      const sqlExpireDate: string = ed.toLocaleDateString() + ' ' + ed.toLocaleTimeString();
      //console.log('retrieving cmdJobsMasterUpdateLimited data starting ' + sqlExpireDate);
  
      let cmd = new Command();
      cmd.procedure = "cmdJobsMasterUpdateLimited";
      cmd.addParameter("ExpireDate", sqlExpireDate);
  
      const JobsMasterULResponse: any = await toPromise(this.db.command(cmd));

      // If nothing was cached in this session yet, seed the cache from IndexedDB
      if (this.ohMapCache.size === 0) {
        await this.loadOhMapCache();
      }
      if (Array.isArray(JobsMasterULResponse)) {
        JobsMasterULResponse.forEach((job) => this.restoreOhMap(job));
        // Rules-based phases (Phase Manager): recalculates what is out of date and adds the pm* fields.
        await this.phaseOverlay.enrichChanged(JobsMasterULResponse);
      }
  
      // Insert bulk data into 'jobs' table
      await this.idb.insertBulk('jobs', JobsMasterULResponse, async () => {
        // console.log(
        //   'done inserting/updating ',
        //   JobsMasterULResponse.length,
        //   ' rows ',
        //   JobsMasterULResponse
        // );
      });
  
      // Clean up
      await this.cleanDeletes(sqlExpireDate, gridApi);
  
      // Apply grid updates
      await this.applyTransactionUpdates(gridApi, JobsMasterULResponse);
  
      // Run gridSync with little delay to allow the indexedDB to update
      setTimeout(async () => {
        if (gridApi) await this.gridSync(gridApi);
      }, 2000);
    } catch (error) {
      console.error('Error in updateLoader:', error);
    }
  }
  

  async gridSync(gridApi: any) {
    if (!gridApi) return;
  
    const indexedDBJobs = await this.idb.list('jobs');
    //console.log('gridSync indexedDBJobs', indexedDBJobs.length)
  
    await this.applyTransactionUpdates(gridApi, indexedDBJobs);
  }

  async applyTransactionUpdates(gridApi, rows) {
    if (!gridApi) return;
    const startMillis = new Date().getTime();
    this.gridUpdatedCount = 0;

    // Create a map of current row nodes for quick access
    const rowNodesMap = new Map();
    gridApi.forEachNode(node => {
        if (node.data?.jobID != null) {
            rowNodesMap.set(String(node.data.jobID), node);
        }
    });

    const toAdd = [];
    const toUpdate = [];

    // Prepare the data for adding and updating
    for (const row of rows) {
        if (!row?.jobID) continue;

        const jobID = String(row.jobID);
        const rowNode = rowNodesMap.get(jobID);

        if (!rowNode) {
            // New row to add (only if it belongs to the subset this screen shows)
            if (!this.rowFilter || this.rowFilter(row)) {
                toAdd.push(row);
            }
            continue;
        }

        // Check for changes
        const changes = this.getChanges(rowNode.data, row);
        if (changes) {
            toUpdate.push({ rowNode, changes });
        }
    }

    // Batch add new rows
    if (toAdd.length > 0 && gridApi) {
        //console.log('applyTransactionUpdates transaction toAdd', toAdd.length);
        gridApi.applyTransactionAsync({ add: toAdd }, this.resultCallback(startMillis));
    }

    // Process updates in batches
    if (toUpdate.length > 0 && gridApi) {
      const totalUpdates = toUpdate.length;
  
      for (let i = 0; i < totalUpdates; i += this.batchSize) {
          const batchUpdates = toUpdate.slice(i, i + this.batchSize);
          const updatedRows = [];
          const rowNodes = [];
  
          for (const { rowNode, changes } of batchUpdates) {
              if (changes) {
                  const updatedData = { ...rowNode.data, ...changes };
                  if (JSON.stringify(rowNode.data) !== JSON.stringify(updatedData)) {
                      rowNode.setData(updatedData); // Update only if there's a change
                      updatedRows.push(updatedData);
                      rowNodes.push(rowNode);
                  }
              }
          }
  
          // Refresh cells only if there are actual updates
          if (rowNodes.length > 0) {
              gridApi.refreshCells({ rowNodes, force: false }); // Consider using force: false
              try {
                //console.log('gridSync transaction updatedRows', updatedRows.length);
                gridApi.applyTransactionAsync({ update: updatedRows }, this.resultCallback(startMillis));
              } catch (err) {
                  console.warn('Group update warning:', err);
              }
          }
  
          if (i + this.batchSize < totalUpdates) {
              await new Promise(resolve => setTimeout(resolve, this.updateThreshold));
          }
      }
    }
  }

  resultCallback(startMillis) {
    this.gridUpdatedCount++;
    if (this.gridUpdatedCount === 500) {
      // print message in next VM turn to allow browser to refresh
      setTimeout(() => {
        const endMillis = new Date().getTime();
        const duration = endMillis - startMillis;
        //console.log("Async took " + duration.toLocaleString() + "ms");
      }, 0);
    }
  }

  getChanges(gridData, dbData) {
    const changes = {};
    let hasChanges = false;

    for (const key in dbData) {
      if (!dbData.hasOwnProperty(key)) continue;
      
      const gridValue = gridData[key];
      const dbValue = dbData[key];

      if (this.isDateField(key)) {
        if (!this.areDatesEqual(gridValue, dbValue)) {
          changes[key] = dbValue;
          hasChanges = true;
        }
      } else if (gridValue !== dbValue) {
        changes[key] = dbValue;
        hasChanges = true;
      }
    }
    return hasChanges ? changes : null;
  }
  

  isDateField(key) {
    return key === 'updatedDate' || key.toLowerCase().includes('date');
  }

  // Helper function to compare dates
  areDatesEqual(date1: Date | string, date2: Date | string): boolean {
    const d1 = date1 instanceof Date ? date1 : new Date(date1);
    const d2 = date2 instanceof Date ? date2 : new Date(date2);
    return d1.getTime() === d2.getTime();
  }

  async cleanDeletes(startDate, gridApi) {
    //cmdJobMasterDeletes
    var cmd: Command = new Command();
    cmd.procedure = "cmdGetInactiveJobIds"
    cmd.addParameter("StartDate", startDate)
  
    try {
      //const retval = await this.db.command(cmd).toPromise();
      const retval: any = await toPromise(this.db.command(cmd));
      // console.log('got deletes', retval);
  
      if (retval.length > 0) {
        const toRemove = [];
        const archives = [];
  
        const deletesString: any = retval[0].deleted;
        if (deletesString.indexOf(',') > 0) {
          toRemove.push(...deletesString.split(',').map(Number));
        } else if (deletesString.length > 0) {
          toRemove.push(parseInt(deletesString));
        }
  
        const archivedString: any = retval[0].archived;
        if (archivedString.indexOf(',') > 0) {
          archives.push(...archivedString.split(',').map(Number));
        } else if (archivedString.length > 0) {
          archives.push(parseInt(archivedString));
        }
  
        const allToRemove = [...toRemove, ...archives];
  
        allToRemove.forEach(async (jobID) => {
          const checkRec = this.dataStore.jobMaster.data.filter((obj: any) => obj.jobID !== jobID);
          if (checkRec.length > 0) {
            this.dataStore.jobMaster.data = this.dataStore.jobMaster.data.filter((obj: any) => obj.jobID !== jobID);
            await this.idb.simpleDelete('jobs', jobID);
            if(gridApi) gridApi.applyTransaction({ remove: [{ jobID: jobID }] });
          }
        });
  
        // Remove error rows (if any)
        await this.idb.simpleDelete('jobs', -2);
        await this.idb.simpleDelete('jobs', -1);
      }
    } catch (error) {
      console.error('Error in cleanDeletes:', error);
    }
  }

  private callback(e) {
    //console.log('callback reload', e)
  }

  // old job master bulk loader
  async bulkLoader(gridApi) {
    let commands = [];
    await this.idb.clearData('jobs');
    this.existingIds = new Set([]);

    gridApi.setGridOption('rowData', []);

    for (let y = 2019; y <= new Date().getFullYear(); y++) {
      if (y > 2022) {
        // Generate weekly intervals for the current year
        let currentDate = new Date(y, 0, 1);
        while (currentDate <= new Date()) {
          const startDate = new Date(currentDate);
          let endDate = new Date(currentDate);
          endDate.setDate(endDate.getDate() + 7);
          if (endDate > new Date()) {
            endDate.setTime(new Date().getTime()); // Set to current date
          }
          endDate = this.setToEndOfDay(endDate);
          commands.push({ startDate, endDate });
          currentDate.setDate(currentDate.getDate() + 7);
        }
      } else {
        // Generate monthly intervals for previous years
        for (let m = 0; m <= 11; m++) {
          const d = new Date(y, m, 1);
          if (d <= new Date()) {
            //console.log('date', d);
            let endDate;
            if (m === 11) {
              endDate = new Date(y + 1, 0, 1);
            } else {
              endDate = new Date(y, m + 1, 1);
            }
            // Set start date to 12:00 AM (midnight)
            const startDate = new Date(d);
            startDate.setHours(0, 0, 0, 0);
            // Set end date to the last minute of the month
            endDate.setHours(23, 59, 59, 999);
            commands.push({ startDate, endDate });
          }
        }
      }
    }
  }

  setToEndOfDay(dateTimeString: Date): Date | null {
    try {
      // Parse the input date string
      const parsedDate = new Date(dateTimeString);

      // Check if parsing was successful
      if (isNaN(parsedDate.getTime())) {
        console.error('Invalid date format. Please provide a valid date and time.');
        return null;
      }

      // Set the time to 11:59 PM
      parsedDate.setHours(23, 59, 0, 0);
      return parsedDate;
    } catch (error) {
      console.error('Error occurred while processing the date:', error);
      return null;
    }
  }


  // Last known oHMap value per jobID, used when the API omits the property
private ohMapCache = new Map<number, any>();

private hasValue(value: any): boolean {
  return value !== undefined && value !== null && value !== '';
}

// Reads the stored jobs from IndexedDB and remembers every non-empty oHMap
private async loadOhMapCache(): Promise<void> {
  try {
    const storedJobs: any[] = await this.idb.list('jobs');
    for (const stored of storedJobs ?? []) {
      if (stored?.jobID != null && this.hasValue(stored.oHMap)) {
        this.ohMapCache.set(Number(stored.jobID), stored.oHMap);
      }
    }
  } catch (err) {
    console.warn('Could not read oHMap from IndexedDB:', err);
  }
}

// Keeps the API value when present, otherwise restores the cached one
private restoreOhMap(job: any): void {
  if (!job || job.jobID == null) return;

  if (this.hasValue(job.oHMap)) {
    this.ohMapCache.set(Number(job.jobID), job.oHMap);
    return;
  }

  const cached = this.ohMapCache.get(Number(job.jobID));
  if (this.hasValue(cached)) {
    job.oHMap = cached;
  }
}

}

// Helper to avoid toPromise() - refactor for A20
export function toPromise<T>(obs$: Observable<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const sub = obs$.subscribe({
      next: (val) => {
        resolve(val);
        sub.unsubscribe();
      },
      error: (err) => reject(err),
    });
  });
  
}